import type { CommonFormProps } from '@ant-design/pro-form/es/BaseForm'
import type { EditableFormState } from '@pkg/form/contexts/editableForm'
import { extractUserMessages, isServerError } from '@pkg/form/utils/errorHandling'
import type { AnyFormApi } from '@tanstack/react-form'
import type { NotificationInstance } from 'antd/es/notification/interface'
import type { StoreApi } from 'zustand/vanilla'

/**
 * Merge the full form props with default submit labels and a default onFinish.
 * - submitter: injects default German labels if none provided
 * - onFinish: if not provided, calls form.handleSubmit() and returns true/false
 */
export function mergeFormProps<TProps extends CommonFormProps<any, any>>(
  form: AnyFormApi,
  props: TProps,
  editable?: StoreApi<EditableFormState>,
  noitification?: NotificationInstance,
): TProps {
  const { submitter, onFinish } = props

  return {
    ...props,
    // Prevent unintended focus/submit behaviors that can cause focus jumps
    isKeyPressSubmit: props.isKeyPressSubmit ?? false,
    autoFocusFirstInput: props.autoFocusFirstInput ?? false,
    submitter:
      typeof submitter === 'object'
        ? {
            ...submitter,
            searchConfig: {
              submitText: submitter?.searchConfig?.submitText ?? 'Speichern',
              resetText: submitter?.searchConfig?.resetText ?? 'Zurücksetzen',
            },
          }
        : false,
    onFinish: !onFinish
      ? async () => {
          const state = editable?.getState()
          try {
            await form.handleSubmit()
            state?.clearSubmitErrors()
            state?.setMeta({ lastSavedAt: Date.now() })
            return true
          } catch (err) {
            if (!isServerError(err)) {
              const messages = extractUserMessages(err)
              state?.setMeta({ submitErrors: messages })
              noitification?.error({
                message: 'Eingabe prüfen',
                description: messages[0] ?? 'Bitte korrigieren Sie die markierten Felder.',
              })
              return false
            }
            throw err
          }
        }
      : onFinish,
  }
}
